import React, { useEffect, useState } from "react";
import {
    Box, CssBaseline, Breadcrumbs, Link, Typography, Stack, Button,
    Paper, Grid, Avatar, Chip, CircularProgress, Divider, Card, CardContent,
    TextField, MenuItem, Snackbar, Alert
} from "@mui/material";
import {
    NavigateNext, Phone, Cake, Wc, Badge, AccountBalance,
    School, CalendarMonth, Edit, ArrowBack,
    Close, Save, Person, Work, Psychology, Clear
} from '@mui/icons-material';
import { useNavigate, useParams } from "react-router-dom";
import Header from "../../component/header/Header";

const colors = {
    pageBg: '#F8F9FA',
    sidebarBg: '#F1F2F6',
    accent: '#FFC107',
    accentDark: '#FFA000',
    tableHeader: '#FDF7E2',
    textMain: '#5D4037',
    white: '#FFFFFF',
    filterBg: "#FFF9E6"
};

const EditStaffScreen = () => {
    const navigation = useNavigate();
    const { id } = useParams();

    const [isEditing, setIsEditing] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

    const [staff, setStaff] = useState(null);
    const [formData, setFormData] = useState({});

    useEffect(() => {
        getStaffDetail();
        localStorage.setItem("current_tab", "Staff");
    }, [id]);

    const getStaffDetail = async () => {
        setLoading(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff/${id}`);
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStaff(resJson.data);
                setFormData(resJson.data);
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error loading data", severity: "error" });
        } finally {
            setLoading(false);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setFormData((prev) => ({ ...prev, [name]: value }));
    };

    const handleSave = async (e) => {
        if (e) e.preventDefault();
        setSaving(true);
        try {
            const response = await fetch(`https://www.geniusmileniaeducation.link/db-api/staff/${id}`, {
                method: 'PUT',
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(formData)
            });
            const resJson = await response.json();
            if (resJson.status === 1) {
                setStaff(formData);
                setIsEditing(false);
                setSnackbar({ open: true, message: "Changes saved successfully!", severity: "success" });
            } else {
                setSnackbar({ open: true, message: resJson.message, severity: "error" });
            }
        } catch (error) {
            setSnackbar({ open: true, message: "Error saving changes", severity: "error" });
        } finally {
            setSaving(false);
        }
    };

    if (loading) return <Box display="flex" justifyContent="center" alignItems="center" minHeight="80vh"><CircularProgress sx={{ color: colors.accent }} /></Box>;
    if (!staff) return <Typography m={4}>Staff record not found.</Typography>;

    const isTutor = staff.role === "Tutor";

    return (
        <Box sx={{ display: 'flex', bgcolor: colors.sidebarBg, minHeight: '100vh' }}>
            <CssBaseline />

            {/* --- VIEW MODE --- */}
            {!isEditing ? (
                <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5 }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box>
                            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                                <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                                <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Staff")} sx={{ cursor: 'pointer' }}>Staff</Link>
                                <Typography color="text.primary">Profile</Typography>
                            </Breadcrumbs>
                            <Header title="Staff Profile" subtitle="Detailed information and employment records" />
                        </Box>
                        <Stack direction="row" spacing={2}>
                            <Button startIcon={<ArrowBack />} onClick={() => navigation("/Portal/Staff")} sx={{ color: colors.textMain }}>Back</Button>
                            <Button variant="contained" startIcon={<Edit />} onClick={() => setIsEditing(true)} sx={{ bgcolor: colors.accent, color: 'black', borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}>Edit Profile</Button>
                        </Stack>
                    </Stack>

                    <Grid container spacing={4}>
                        {/* LEFT COLUMN: Resume Card */}
                        <Grid item xs={12} md={4}>
                            <Paper elevation={0} sx={{ p: 4, borderRadius: 4, border: '1px solid #F0E6CC', textAlign: 'center', bgcolor: colors.pageBg }}>
                                <Avatar sx={{ width: 120, height: 120, mx: 'auto', mb: 2, bgcolor: colors.accent, color: 'black', fontSize: '2rem', fontWeight: 'bold' }}>{staff.fullname?.charAt(0)}</Avatar>
                                <Typography variant="h5" fontWeight="bold">{staff.fullname}</Typography>
                                <Typography variant="body2" color="text.secondary" gutterBottom>{staff.email || 'No email provided'}</Typography>
                                <Stack direction="row" spacing={1} justifyContent="center" mt={2}>
                                    <Chip label={staff.role} sx={{ bgcolor: 'black', color: 'white', fontWeight: 'bold' }} />
                                    <Chip label={staff.status === "A" ? "Active" : "Inactive"} color={staff.status === "A" ? "success" : "default"} sx={{ fontWeight: 'bold' }} />
                                </Stack>
                                <Divider sx={{ my: 3 }} />
                                <Stack spacing={2} textAlign="left">
                                    <Box display="flex" alignItems="center" gap={2}><Phone sx={{ color: colors.accent }} fontSize="small" /><Box><Typography variant="caption" display="block" color="text.secondary">Mobile Number</Typography><Typography variant="body2" fontWeight="bold">{staff.mobileNo || '-'}</Typography></Box></Box>
                                    <Box display="flex" alignItems="center" gap={2}><Wc sx={{ color: colors.accent }} fontSize="small" /><Box><Typography variant="caption" display="block" color="text.secondary">Gender</Typography><Typography variant="body2" fontWeight="bold">{staff.gender === "M" ? "Male" : "Female"}</Typography></Box></Box>
                                    <Box display="flex" alignItems="center" gap={2}><Cake sx={{ color: colors.accent }} fontSize="small" /><Box><Typography variant="caption" display="block" color="text.secondary">Date of Birth</Typography><Typography variant="body2" fontWeight="bold">{staff.dob || '-'}</Typography></Box></Box>
                                    <Box display="flex" alignItems="center" gap={2}><School sx={{ color: colors.accent }} fontSize="small" /><Box><Typography variant="caption" display="block" color="text.secondary">Education</Typography><Typography variant="body2" fontWeight="bold">{staff.education || '-'}</Typography></Box></Box>
                                </Stack>
                            </Paper>
                        </Grid>

                        {/* RIGHT COLUMN: Details */}
                        <Grid item xs={12} md={8}>
                            <Stack spacing={3}>
                                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}><Badge fontSize="small" /><Typography fontWeight="bold">Employment Details</Typography></Box>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary" display="block">Staff ID</Typography><Typography variant="body2" fontWeight="bold">{staff.staffId}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary" display="block">Joined Date</Typography><Typography variant="body2" fontWeight="bold">{staff.registerDate || '-'}</Typography></Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}><AccountBalance fontSize="small" /><Typography fontWeight="bold">Financial Information</Typography></Box>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary" display="block">Bank Name</Typography><Typography variant="body1" fontWeight="bold" sx={{ color: colors.textMain }}>{staff.bankName || "Not Provided"}</Typography></Grid>
                                            <Grid item xs={12} sm={6}><Typography variant="caption" color="text.secondary" display="block">Bank Account Number</Typography><Typography variant="body1" fontWeight="bold" sx={{ letterSpacing: 1 }}>{staff.bankAccountNo || "Not Provided"}</Typography></Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}><Work fontSize="small" /><Typography fontWeight="bold">Professional Experience</Typography></Box>
                                    <CardContent><Typography variant="body2" sx={{ whiteSpace: 'pre-line', lineHeight: 1.6 }}>{staff.experience || 'No experience details added.'}</Typography></CardContent>
                                </Card>
                                <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}><Psychology fontSize="small" /><Typography fontWeight="bold">Skills & Competencies</Typography></Box>
                                    <CardContent>
                                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{staff.skills?.split(',').map((skill, i) => (<Chip key={i} label={skill.trim()} variant="outlined" sx={{ borderRadius: 1, border: '1px solid #F0E6CC' }} />)) || 'N/A'}</Box>
                                    </CardContent>
                                </Card>
                                {isTutor && (
                                    <Card elevation={0} sx={{ borderRadius: 4, border: '1px solid #F0E6CC' }}>
                                        <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}><School fontSize="small" /><Typography fontWeight="bold">Teaching Assignments</Typography></Box>
                                        <CardContent>
                                            {staff.course && staff.course.length > 0 ? (
                                                <Grid container spacing={2}>
                                                    {staff.course.map((cls, index) => (
                                                        <Grid item xs={12} sm={6} key={index}>
                                                            <Paper variant="outlined" sx={{ p: 2, borderRadius: 2, bgcolor: '#FFF' }}>
                                                                <Typography variant="subtitle2" fontWeight="bold" color="primary">{cls.courseName}</Typography>
                                                                <Stack direction="row" spacing={1} mt={1} alignItems="center"><CalendarMonth sx={{ fontSize: 14, color: 'text.secondary' }} /><Typography variant="caption">{cls.day} • {cls.time}</Typography></Stack>
                                                            </Paper>
                                                        </Grid>
                                                    ))}
                                                </Grid>
                                            ) : <Typography variant="body2" color="text.secondary">No classes assigned.</Typography>}
                                        </CardContent>
                                    </Card>
                                )}
                            </Stack>
                        </Grid>
                    </Grid>
                </Box>
            ) : (

                // --- EDIT MODE (Standardized Layout) ---
                <Box component="main" sx={{ flexGrow: 1, p: 3, bgcolor: "white", m: 2, borderRadius: 5, border: '1px solid #F0E6CC' }}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
                        <Box>
                            <Breadcrumbs separator={<NavigateNext fontSize="small" />} sx={{ mb: 1 }}>
                                <Link underline="hover" color="inherit" onClick={() => navigation("/Portal")} sx={{ cursor: 'pointer' }}>Portal</Link>
                                <Link underline="hover" color="inherit" onClick={() => navigation("/Portal/Staff")} sx={{ cursor: 'pointer' }}>Staff</Link>
                                <Typography color="text.primary">Edit Profile</Typography>
                            </Breadcrumbs>
                            <Header title="Edit Staff Profile" subtitle={`Updating record for ${formData.fullname}`} />
                        </Box>
                        <Button startIcon={<Close />} onClick={() => { setIsEditing(false); setFormData(staff); }} color="inherit">Cancel</Button>
                    </Stack>

                    <form onSubmit={handleSave}>
                        <Grid container spacing={3}>

                            {/* 1. PERSONAL DETAILS */}
                            <Grid item xs={12} md={8}>
                                <Paper elevation={0} sx={{ p: 3, bgcolor: colors.filterBg, borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                        <Person sx={{ color: colors.textMain }} />
                                        <Typography variant="h6" fontWeight="bold">Personal Information</Typography>
                                    </Stack>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Full Name" name="fullname" value={formData.fullname} onChange={handleChange} size="small" required sx={{ bgcolor: 'white' }} />
                                        </Grid>
                                        <Grid item xs={12} md={6}>
                                            <TextField fullWidth label="Email Address" name="email" type="email" value={formData.email} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth label="Mobile Number" name="mobileNo" value={formData.mobileNo} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }} />
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField select fullWidth label="Gender" name="gender" value={formData.gender} onChange={handleChange} size="small" sx={{ bgcolor: 'white' }}>
                                                <MenuItem value="M">Male</MenuItem>
                                                <MenuItem value="F">Female</MenuItem>
                                            </TextField>
                                        </Grid>
                                        <Grid item xs={12} md={4}>
                                            <TextField fullWidth label="Date of Birth" name="dob" type="date" value={formData.dob} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} sx={{ bgcolor: 'white' }} />
                                        </Grid>
                                        <Grid item xs={12}>
                                            <TextField fullWidth label="Education / Qualifications" name="education" value={formData.education} onChange={handleChange} size="small" placeholder="e.g. Bachelor in Education, UM" sx={{ bgcolor: 'white' }} />
                                        </Grid>
                                    </Grid>
                                </Paper>
                            </Grid>

                            {/* 2. ROLE & STATUS */}
                            <Grid item xs={12} md={4}>
                                <Paper elevation={0} sx={{ p: 3, border: '1px solid #F0E6CC', borderRadius: 3, height: '100%' }}>
                                    <Stack direction="row" spacing={1} alignItems="center" mb={3}>
                                        <Badge sx={{ color: colors.accent }} />
                                        <Typography variant="h6" fontWeight="bold">Role & Access</Typography>
                                    </Stack>
                                    <Stack spacing={2.5}>
                                        <TextField select fullWidth label="Staff Role" name="role" value={formData.role} onChange={handleChange} size="small">
                                            <MenuItem value="Admin">Admin</MenuItem>
                                            <MenuItem value="Tutor">Tutor</MenuItem>
                                            <MenuItem value="IT">IT Support</MenuItem>
                                        </TextField>
                                        <TextField select fullWidth label="Status" name="status" value={formData.status} onChange={handleChange} size="small">
                                            <MenuItem value="A">Active</MenuItem>
                                            <MenuItem value="I">Inactive</MenuItem>
                                        </TextField>
                                        <TextField fullWidth label="Joined Date" name="registerDate" type="date" value={formData.registerDate} onChange={handleChange} size="small" InputLabelProps={{ shrink: true }} />
                                    </Stack>
                                </Paper>
                            </Grid>

                            {/* 3. EXPERIENCE & SKILLS */}
                            <Grid item xs={12} md={6}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Work fontSize="small" />
                                        <Typography fontWeight="bold">Professional Experience</Typography>
                                    </Box>
                                    <CardContent>
                                        <TextField fullWidth multiline rows={4} name="experience" value={formData.experience} onChange={handleChange} placeholder="Briefly describe previous work history..." />
                                    </CardContent>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: '1px solid #F0E6CC' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <Psychology fontSize="small" />
                                        <Typography fontWeight="bold">Skills & Competencies</Typography>
                                    </Box>
                                    <CardContent>
                                        <TextField fullWidth multiline rows={4} name="skills" value={formData.skills} onChange={handleChange} placeholder="List skills separated by commas (e.g. Maths, Coaching, Management)" />
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* 4. BANKING (Conditional for Tutors) */}
                            <Grid item xs={12}>
                                <Card elevation={0} sx={{ borderRadius: 3, border: `1px solid ${formData.role === 'Tutor' ? colors.accent : '#F0E6CC'}`, bgcolor: formData.role === 'Tutor' ? '#FFFEF5' : '#FFF' }}>
                                    <Box sx={{ p: 2, bgcolor: colors.tableHeader, display: 'flex', alignItems: 'center', gap: 1 }}>
                                        <AccountBalance fontSize="small" />
                                        <Typography fontWeight="bold">Banking Information {formData.role !== 'Tutor' && '(Optional)'}</Typography>
                                    </Box>
                                    <CardContent>
                                        <Grid container spacing={3}>
                                            <Grid item xs={12} md={6}>
                                                <TextField fullWidth label="Bank Name" name="bankName" value={formData.bankName} onChange={handleChange} size="small" placeholder="e.g. MAYBANK" />
                                            </Grid>
                                            <Grid item xs={12} md={6}>
                                                <TextField fullWidth label="Account Number" name="bankAccountNo" value={formData.bankAccountNo} onChange={handleChange} size="small" />
                                            </Grid>
                                        </Grid>
                                    </CardContent>
                                </Card>
                            </Grid>

                            {/* SUBMIT BUTTONS */}
                            <Grid item xs={12}>
                                <Divider sx={{ my: 2 }} />
                                <Stack direction="row" spacing={2} justifyContent="flex-end">
                                    <Button variant="outlined" onClick={() => { setIsEditing(false); setFormData(staff); }} startIcon={<Clear />} sx={{ borderRadius: 2 }}>Discard Changes</Button>
                                    <Button
                                        type="submit"
                                        variant="contained"
                                        disabled={saving}
                                        startIcon={saving ? <CircularProgress size={20} color="inherit" /> : <Save />}
                                        sx={{ bgcolor: colors.accent, color: 'black', px: 6, borderRadius: 2, '&:hover': { bgcolor: colors.accentDark } }}
                                    >
                                        {saving ? "Saving..." : "Update Profile"}
                                    </Button>
                                </Stack>
                            </Grid>

                        </Grid>
                    </form>
                </Box>
            )}

            <Snackbar open={snackbar.open} autoHideDuration={4000} onClose={() => setSnackbar({ ...snackbar, open: false })} anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}>
                <Alert severity={snackbar.severity} variant="filled" sx={{ borderRadius: 2 }}>{snackbar.message}</Alert>
            </Snackbar>
        </Box>
    );
};

export default EditStaffScreen;